import { useQuery } from "convex/react";
import { MessageSquareIcon } from "lucide-react";
import { Navigate, useLocation, useParams } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ThreadCard } from "@/modules/threads/ThreadCard";
import { ThreadDetail } from "@/modules/threads/ThreadDetail";
import { api } from "../../../convex/_generated/api";

function useBasePath() {
	const { pathname } = useLocation();
	return pathname.startsWith("/dev/workspace")
		? "/dev/workspace"
		: "/workspace";
}

export default function ThreadsPage() {
	const { id: selectedId } = useParams<{ id?: string }>();
	const basePath = useBasePath();
	const threads = useQuery(api.agent.queries.listThreads) ?? [];
	const hasThreads = threads.length > 0;
	const activeId = selectedId ?? threads[0]?.clericalThreadId;

	if (!selectedId && hasThreads) {
		return (
			<Navigate
				to={`${basePath}/threads/${encodeURIComponent(threads[0].clericalThreadId)}`}
				replace
			/>
		);
	}

	return (
		<div className="flex h-full flex-col">
			<header className="flex items-center justify-between border-b py-3 pl-14 pr-6">
				<div>
					<h1 className="text-lg font-semibold">Threads</h1>
					<p className="text-sm text-muted-foreground">
						Inspect agent threads, messages, tool calls, and sub-threads.
					</p>
				</div>
				<Badge variant="secondary">{threads.length} threads</Badge>
			</header>
			<div className="flex min-h-0 flex-1">
				{/* Left panel — thread list */}
				<div className="flex w-[360px] shrink-0 flex-col border-r">
					<div className="flex items-center gap-2 border-b px-4 py-3 text-sm text-muted-foreground">
						<MessageSquareIcon className="size-4" />
						<span>Root threads</span>
					</div>
					<ScrollArea className="h-full">
						<div className="space-y-2 p-3">
							{threads.length > 0 ? (
								threads.map((thread) => (
									<ThreadCard
										key={thread._id}
										thread={thread}
										to={`${basePath}/threads/${encodeURIComponent(thread.clericalThreadId)}`}
										selected={thread.clericalThreadId === activeId}
									/>
								))
							) : (
								<Card size="sm">
									<CardContent className="text-sm text-muted-foreground">
										No threads yet. Threads are created when the agent processes
										incoming messages.
									</CardContent>
								</Card>
							)}
						</div>
					</ScrollArea>
				</div>
				{/* Right panel — thread detail */}
				<div className="min-w-0 flex-1 overflow-hidden">
					{!hasThreads ? (
						<div className="flex h-full items-center justify-center p-6">
							<Card className="max-w-md">
								<CardContent className="text-center text-sm text-muted-foreground">
									No threads yet. Send an email or message to create one.
								</CardContent>
							</Card>
						</div>
					) : activeId ? (
						<ThreadDetail clericalThreadId={decodeURIComponent(activeId)} />
					) : (
						<div className="flex h-full items-center justify-center text-sm text-muted-foreground">
							Select a thread to inspect.
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
